import type { AppLocale } from "@/lib/i18n/constants";
import { ALL_APP_LOCALES } from "@/lib/i18n/constants";
import { getPersonas } from "@/lib/personas/registry";

export const COOKIE_CONSENT_STORAGE_KEY = "seoh-consent-v1";
export const COOKIE_CONSENT_CHANGE_EVENT = "seoh:consentchange";
export const FIRST_PAINT_DEFAULT_THEME = "dark";
export const FIRST_PAINT_DEFAULT_PERSONA = "none";
export const FIRST_PAINT_PERSONA_THEME = "staged";

export const NON_ESSENTIAL_CONSENT_LOCALES = ALL_APP_LOCALES;
const nonEssentialConsentLocaleSet = new Set<string>(
  NON_ESSENTIAL_CONSENT_LOCALES,
);

type ConsentBootstrapInput = {
  readonly locale: AppLocale;
  readonly gaId?: string;
};

export function requiresNonEssentialConsent(locale: string): boolean {
  return nonEssentialConsentLocaleSet.has(locale);
}

export function buildFirstPaintInitScript(locale: AppLocale): string {
  const payload = JSON.stringify({
    defaultPersona: FIRST_PAINT_DEFAULT_PERSONA,
    locales: ALL_APP_LOCALES,
    personaTheme: FIRST_PAINT_PERSONA_THEME,
    personas: getPersonas().map((persona) => ({
      id: persona.id,
      slug: persona.slug,
    })),
    required: requiresNonEssentialConsent(locale),
    storageKey: COOKIE_CONSENT_STORAGE_KEY,
    themeKeys: ["seoh-theme", "theme"],
  });

  return `(()=>{try{var c=${payload},d=document.documentElement,s=location.pathname.split("/").filter(Boolean),f=s[0]&&c.locales.indexOf(s[0])>-1?1:0,g=s[f]||"",p=c.defaultPersona;for(var i=0;i<c.personas.length;i++){if(c.personas[i].slug===g){p=c.personas[i].id;break}}d.setAttribute("data-persona",p);d.setAttribute("data-persona-theme",c.personaTheme);var a=!c.required;if(c.required){var r=localStorage.getItem(c.storageKey),j=r?JSON.parse(r):null;a=!!(j&&j.version===1&&j.preferences===true)}if(a){for(var k=0;k<c.themeKeys.length;k++){var t=localStorage.getItem(c.themeKeys[k]);if(t==="light"||t==="dark"){d.setAttribute("data-bs-theme",t);break}}}}catch(e){}})();`;
}

export function buildThemeInitScript(locale: AppLocale): string {
  return buildFirstPaintInitScript(locale);
}

export function buildConsentBootstrapScript({
  locale,
  gaId,
}: ConsentBootstrapInput): string {
  const payload = JSON.stringify({
    adultGateKeys: ["seoh_adult_gate", "seoh_adult_gate_expires_at"],
    eventName: COOKIE_CONSENT_CHANGE_EVENT,
    legacyThemeKey: "theme",
    gaId: gaId ?? "",
    optionalExactKeys: ["seoh:funnel-planner-game-visible-v3"],
    optionalPrefixes: ["seoh_", "seoh-"],
    required: requiresNonEssentialConsent(locale),
    storageKey: COOKIE_CONSENT_STORAGE_KEY,
  });

  return `(()=>{const c=${payload};const d={preferences:!c.required,analytics:!c.required,marketing:!c.required};const n=(v={})=>({version:1,decided:v.decided===true,preferences:v.preferences===true,analytics:v.analytics===true,marketing:v.marketing===true&&navigator.globalPrivacyControl!==true,updatedAt:typeof v.updatedAt==="string"?v.updatedAt:""});const r=()=>{if(!c.required)return n({...d,decided:true});try{const raw=localStorage.getItem(c.storageKey);if(!raw)return n({...d,decided:false});const parsed=JSON.parse(raw);return parsed&&parsed.version===1?n(parsed):n({...d,decided:false})}catch(e){return n({...d,decided:false})}};const x=()=>{try{const keep=new Set([c.storageKey,...c.adultGateKeys]);const remove=[];for(let i=0;i<localStorage.length;i+=1){const key=localStorage.key(i);if(key&&!keep.has(key)&&(c.optionalExactKeys.includes(key)||c.optionalPrefixes.some((prefix)=>key.startsWith(prefix))))remove.push(key)}remove.concat(c.legacyThemeKey).forEach((key)=>localStorage.removeItem(key))}catch(e){}};window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments)};const m=(s)=>({ad_storage:"denied",ad_user_data:"denied",ad_personalization:"denied",analytics_storage:s.analytics?"granted":"denied",functionality_storage:s.preferences?"granted":"denied",personalization_storage:s.preferences?"granted":"denied",security_storage:"granted",wait_for_update:c.required&&!s.decided?500:0});const l=()=>{if(!c.gaId||window.__seohGaLoaded)return;window.__seohGaLoaded=true;const s=document.createElement("script");s.async=true;s.src="https://www.googletagmanager.com/gtag/js?id="+encodeURIComponent(c.gaId);document.head.appendChild(s);window.gtag("js",new Date());window.gtag("config",c.gaId)};const a=(command)=>{const state=r();window.gtag("consent",command,m(state));if(state.analytics)l();return state};const w=(next)=>{const state=n({...next,decided:true,updatedAt:new Date().toISOString()});try{localStorage.setItem(c.storageKey,JSON.stringify(state));if(!state.preferences)x()}catch(e){}a("update");window.dispatchEvent(new CustomEvent(c.eventName,{detail:state}));return state};window.__seohConsent={required:c.required,storageKey:c.storageKey,eventName:c.eventName,current:r,has:(category)=>r()[category]===true,set:w,acceptAll:()=>w({preferences:true,analytics:true,marketing:true}),rejectAll:()=>w({preferences:false,analytics:false,marketing:false})};a("default")})();`;
}
